import {AsyncInlineError} from '@s-ui/decorators'

import AdsMappersFactory from '../../ads/Mappers/factory.js'
import AdsValueObjectsFactory from '../../ads/ValueObjects/factory.js'
import {FetchOrFail} from '../../fetcher/Fetchers/FetchOrFail/index.js'
import PageValueObjectsFactory from '../../page/ValueObjects/factory.js'
import {FromRawsAIFaqsToFaqsListVOMapper} from '../../seo/Mappers/FromRawsAIFaqsToFaqsListVOMapper.js'
import {DomainSharedError} from '../../shared/Errors/DomainSharedError.js'
import {GetAggregationsSearchError} from '../Errors/GetAggregationsSearchError.js'
import {GetFaqsForUrlSearchError} from '../Errors/GetFaqsForUrlSearchError.js'
import {GetSearchDataFromUrlError} from '../Errors/GetSearchDataFromUrlError.js'
import {SearchMappersFactory} from '../Mappers/factory.js'
import {DataSearchValueObject} from '../ValueObjects/DataSearchValueObject.js'
import {FiltersAggregationsSearchValueObject} from '../ValueObjects/FiltersAggregationsSearchValueObject.js'
import {ResultsSearchValueObject} from '../ValueObjects/ResultsSearchValueObject.js'
import {SearchRepository} from './SearchRepository.js'

export class HttpSearchRepository extends SearchRepository {
  _getCustomHeaders({variant}) {
    if (variant) return {'X-Adevinta-MT-Variant': variant}
  }

  #apiUrl
  #config
  #errors
  #fetchOrFail

  // These are a temporary variables that will be removed once the new search is fully implemented
  // Related tech-debt: https://jira.ets.mpi-internal.com/browse/MTR-94416
  #fromSubscriptionUrlVOToSearchRequestVOMapper
  #httpSearchUrlResponseToSearchDataAdsValueObjectMapper
  #searchDataAdsValueObject
  #urlValueObject

  constructor({
    config,
    fetchOrFail,
    // These are a temporary variables that will be removed once the new search is fully implemented
    // Related tech-debt: https://jira.ets.mpi-internal.com/browse/MTR-94416
    fromSubscriptionUrlVOToSearchRequestVOMapper,
    httpSearchUrlResponseToSearchDataAdsValueObjectMapper,
    searchDataAdsValueObject,
    urlValueObject
  }) {
    super()
    this.#config = config
    this.#fetchOrFail = fetchOrFail
    this.#apiUrl = this.#config.get('API_URL')
    this.#errors = this.#config.get('COMMON').ERRORS

    // These are a temporary variables that will be removed once the new search is fully implemented
    // Related tech-debt: https://jira.ets.mpi-internal.com/browse/MTR-94416
    this.#fromSubscriptionUrlVOToSearchRequestVOMapper = fromSubscriptionUrlVOToSearchRequestVOMapper
    this.#httpSearchUrlResponseToSearchDataAdsValueObjectMapper = httpSearchUrlResponseToSearchDataAdsValueObjectMapper
    this.#searchDataAdsValueObject = searchDataAdsValueObject
    this.#urlValueObject = urlValueObject
  }

  static create({config}) {
    return new HttpSearchRepository({
      config,
      fetchOrFail: FetchOrFail.create({
        config
      }),
      // These are a temporary variables that will be removed once the new search is fully implemented
      // Related tech-debt: https://jira.ets.mpi-internal.com/browse/MTR-94416
      fromSubscriptionUrlVOToSearchRequestVOMapper: SearchMappersFactory.fromSubscriptionUrlVOToSearchRequestVOMapper({
        config
      }),
      httpSearchUrlResponseToSearchDataAdsValueObjectMapper:
        AdsMappersFactory.httpSearchUrlResponseToSearchDataAdsValueObjectMapper({
          config
        }),
      searchDataAdsValueObject: AdsValueObjectsFactory.searchDataAdsValueObject,
      urlValueObject: PageValueObjectsFactory.urlValueObject
    })
  }

  /**
   * @param {Object} params
   * @param {String} params.url
   * @return {Promise}
   */
  @AsyncInlineError()
  async getSearchDataFromUrl({urlVO}) {
    const {GENERIC_ERROR} = this.#errors
    const [error, response] = await this.#fetchOrFail
      .withErrorsFactories({
        [GENERIC_ERROR]: () =>
          GetSearchDataFromUrlError.create('[HttpSearchRepository#getSearchDataFromUrl] Url can not be resolved')
      })
      // Necessary until backend standardizes the listingSectionId for renting vehicles in stock page
      .get(`${this.#apiUrl}/semantic${urlVO.url.replace('/renting/concesionario/', '/concesionario/')}`)

    if (error) return Promise.reject(error)

    return DataSearchValueObject.createFromApi({...response, config: this.#config, url: urlVO.url})
  }

  @AsyncInlineError()
  async getAggregationsFromSearch({searchRequest}) {
    const {GENERIC_ERROR} = this.#errors

    const [error, response] = await this.#fetchOrFail
      .withErrorsFactories({
        [GENERIC_ERROR]: () =>
          GetAggregationsSearchError.create('[HttpSearchRepository#getAggregationsFromSearch] Url can not be resolved')
      })
      .post(`${this.#apiUrl}/search/aggregations`, searchRequest.getRequest())

    if (error) return Promise.reject(error)

    return FiltersAggregationsSearchValueObject.createFromApi({
      aggregations: response.data.aggregations
    })
  }

  @AsyncInlineError()
  async getSearchRequestFromAI({config, searchRequest, variant}) {
    const {GENERIC_ERROR} = this.#errors
    const {NO_CONTENT, GATEWAY_TIMEOUT, INTERNAL_SERVER_ERROR, UNAVAILABLE_FOR_LEGAL_REASONS, UNPROCESSABLE_CONTENT} =
      this.#config.get('COMMON').HTTP_STATUS_CODES

    // Build headers conditionally based on the variant
    const headers = this._getCustomHeaders({variant})

    const [error, response] = await this.#fetchOrFail
      .withErrorsFactories({
        [GATEWAY_TIMEOUT]: () => DomainSharedError.createGatewayTimeoutError({config}),
        [GENERIC_ERROR]: () => DomainSharedError.createGenericError({config}),
        [INTERNAL_SERVER_ERROR]: () => DomainSharedError.createInternalServerError({config}),
        [UNAVAILABLE_FOR_LEGAL_REASONS]: () => DomainSharedError.createUnavailableForLegalReasonsError({config}),
        [UNPROCESSABLE_CONTENT]: () => DomainSharedError.createUnprocessableContentError({config})
      })
      .post(`${this.#apiUrl}/search/suggestion`, searchRequest.value, headers ? {headers} : undefined)

    if (error) return Promise.reject(error)

    if (response.status === NO_CONTENT) {
      return Promise.reject(DomainSharedError.createNoContentError({config}))
    }

    return DataSearchValueObject.createFromApi({...response, config: this.#config})
  }

  // This a temporary method that will be removed once the new search is fully implemented
  // Related tech-debt: https://jira.ets.mpi-internal.com/browse/MTR-94416
  @AsyncInlineError()
  async getRentingSearchRequestFromAI({config, searchRequest, url, variant}) {
    const {GENERIC_ERROR} = this.#errors
    const {NO_CONTENT, GATEWAY_TIMEOUT, INTERNAL_SERVER_ERROR, UNAVAILABLE_FOR_LEGAL_REASONS, UNPROCESSABLE_CONTENT} =
      this.#config.get('COMMON').HTTP_STATUS_CODES

    // Build headers conditionally based on the variant
    const headers = this._getCustomHeaders({variant})

    const [error, response] = await this.#fetchOrFail
      .withErrorsFactories({
        [GATEWAY_TIMEOUT]: () => DomainSharedError.createGatewayTimeoutError({config}),
        [GENERIC_ERROR]: () => DomainSharedError.createGenericError({config}),
        [INTERNAL_SERVER_ERROR]: () => DomainSharedError.createInternalServerError({config}),
        [UNAVAILABLE_FOR_LEGAL_REASONS]: () => DomainSharedError.createUnavailableForLegalReasonsError({config}),
        [UNPROCESSABLE_CONTENT]: () => DomainSharedError.createUnprocessableContentError({config})
      })
      .post(`${this.#apiUrl}/search/suggestion`, searchRequest.value, headers ? {headers} : undefined)

    if (error) return Promise.reject(error)

    if (response.status === NO_CONTENT) {
      return Promise.reject(DomainSharedError.createNoContentError({config}))
    }

    const searchDataValueObject = this.#httpSearchUrlResponseToSearchDataAdsValueObjectMapper.map(response.data)

    const searchRequestVO = this.#fromSubscriptionUrlVOToSearchRequestVOMapper
      .setParams({
        semanticsSearchData: searchDataValueObject.toJSON()
      })
      .map(
        this.#urlValueObject({
          // The path carries the listing segment (targetBuyer, vehicle state, keyword), which the AI
          // response does not return and the rebuilt url would otherwise drop.
          url: url || '/renting/'
        })
      )

    return this.#searchDataAdsValueObject({
      ...searchDataValueObject.toJSON(),
      search: searchRequestVO.toJSON()
    })
  }

  @AsyncInlineError()
  async getResultsFromSearch({config, search}) {
    const {GENERIC_ERROR} = this.#errors

    const [error, response] = await this.#fetchOrFail
      .withErrorsFactories({
        [GENERIC_ERROR]: () => DomainSharedError.createGenericError({config})
      })
      .post(`${config.get('API_URL')}/search/listing`, search.getSearchFromFilters())

    if (error) return Promise.reject(error)

    return ResultsSearchValueObject.createFromListingApi({config: this.#config, data: response.data})
  }

  @AsyncInlineError()
  async getFaqsForUrlSearch({getFaqsRequest}) {
    const {GENERIC_ERROR} = this.#errors
    const {NOT_FOUND, NO_CONTENT} = this.#config.get('COMMON').HTTP_STATUS_CODES

    const targetUrl = getFaqsRequest.value.targetUrl

    const [error, response] = await this.#fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: () => GetFaqsForUrlSearchError.createNotFound(),
        [GENERIC_ERROR]: () =>
          GetFaqsForUrlSearchError.create('[HttpSearchRepository#getFaqsForUrlSearch] Error fetching FAQs')
      })
      .get(`${this.#apiUrl}/faqs/target-url?targetUrl=${targetUrl}`)

    if (error) return Promise.reject(error)

    // 204 is a 2xx and never throws inside FetchOrFail, so it must be detected here, not via withErrorsFactories
    if (response.status === NO_CONTENT) {
      return Promise.reject(GetFaqsForUrlSearchError.createNoContent())
    }

    const fromRawsAIFaqsToFaqsListVOMapper = FromRawsAIFaqsToFaqsListVOMapper.create({config: this.#config})

    return fromRawsAIFaqsToFaqsListVOMapper.map({
      faqs: response.data.faqs || [],
      path: getFaqsRequest.value.targetUrl
    })
  }

  @AsyncInlineError()
  async getAIFaqsForUrlSearch({getAIFaqsRequest, path}) {
    const {GENERIC_ERROR} = this.#errors
    const {NOT_FOUND, NO_CONTENT} = this.#config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this.#fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: () => GetFaqsForUrlSearchError.createNotFound('AI FAQs not found for target URL'),
        [GENERIC_ERROR]: () =>
          GetFaqsForUrlSearchError.create('[HttpSearchRepository#getAIFaqsForUrlSearch] Error fetching AI FAQs')
      })
      .post(`${this.#apiUrl}/faqs/resolve`, getAIFaqsRequest.value)

    if (error) return Promise.reject(error)

    // 204 is a 2xx and never throws inside FetchOrFail, so it must be detected here, not via withErrorsFactories
    if (response.status === NO_CONTENT) {
      return Promise.reject(GetFaqsForUrlSearchError.createNoContent('AI FAQs not generated yet for this URL'))
    }

    const fromRawsAIFaqsToFaqsListVOMapper = FromRawsAIFaqsToFaqsListVOMapper.create({config: this.#config})

    return fromRawsAIFaqsToFaqsListVOMapper.map({
      faqs: response.data.faqs || [],
      path
    })
  }
}
